import moment from 'moment/moment';
import PropTypes from 'prop-types';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import InputField from '../../components/InputField';
import LoadingButton from '../../components/LoadingButton';
import PageLayout from '../../components/PageLayout';
import analytics from '../../lib/analytics';
import { fullDateFormat } from '../../lib/dateFormatter';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';

import './index.scss';

const formatDateField = value => {
  if (!value) return '';
  return value < 10 ? `0${value}` : value.toString();
};

const DateOfBirthPage = ({ dateOfBirth, dispatchAddPageData, dispatchWizardNav }) => {
  const [error, setError] = useState(null);

  const [selectedDay, setSelectedDay] = useState(formatDateField(Number(dateOfBirth?.day)));
  const [selectedMonth, setSelectedMonth] = useState(
    dateOfBirth?.month || dateOfBirth?.month === 0
      ? formatDateField(Number(dateOfBirth?.month) + 1)
      : '',
  );
  const [selectedYear, setSelectedYear] = useState(formatDateField(Number(dateOfBirth?.year)));

  const monthRef = useRef(null);
  const yearRef = useRef(null);

  const [date, setDate] = useState(null);

  const isInvalid = useMemo(() => {
    if (!date) return true;

    const _date = moment(date, 'DD/MM/YYYY');

    if (!_date.isValid()) {
      setError('Please enter a valid date.');
      return true;
    }

    if (_date.add(18, 'years').isAfter(moment())) {
      setError('You must be at least 18 years old.');
      return true;
    }

    setError(null);
    return false;
  }, [date]);

  const handleSubmit = event => {
    event.preventDefault();
    if (isInvalid) return;

    analytics.sendEvent('Click - Next', 'date-of-birth');

    const dates = date.split('/');
    const month = Number(dates[1]) - 1;
    const day = Number(dates[0]);
    const year = Number(dates[2]);
    const payload = payloadFactory.createAddPageDataPayload(
      'dateOfBirth',
      { day, month, year },
      'dateOfBirth',
      fullDateFormat(moment(date, 'DD/MM/YYYY').toDate()),
    );

    dispatchAddPageData(payload);
    dispatchWizardNav({ dir: 'fwd' });
  };

  useEffect(() => {
    if (selectedDay && selectedMonth && selectedYear && selectedYear.length === 4) {
      const date = `${selectedDay}/${selectedMonth}/${selectedYear}`;
      setDate(date);
    } else {
      setDate(null);
    }
  }, [selectedDay, selectedMonth, selectedYear]);

  const handleDayChange = e => {
    setSelectedDay(e.target.value);
    if (e.target.value.length === 2) monthRef.current.focus();
  };

  const handleMonthChange = e => {
    setSelectedMonth(e.target.value);
    if (e.target.value.length === 2) yearRef.current.focus();
  };

  return (
    <PageLayout
      title="What is your date of birth?"
      helpText="You need to be 18 years or older in order to be eligible to apply for a loan."
    >
      <div className="date-of-birth">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col col--4/16">
              <InputField
                label="Day"
                id="day"
                placeholder="DD"
                type="number"
                min={1}
                max={31}
                value={selectedDay}
                onChange={handleDayChange}
              />
            </div>
            <div className="col col--4/16">
              <InputField
                label="Month"
                id="month"
                placeholder="MM"
                type="number"
                min={1}
                max={12}
                value={selectedMonth}
                onChange={handleMonthChange}
                ref={monthRef}
              />
            </div>
            <div className="col col--6/16">
              <InputField
                label="Year"
                id="year"
                placeholder="YYYY"
                type="number"
                min={1900}
                max={new Date().getFullYear()}
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value)}
                ref={yearRef}
              />
            </div>
          </div>
          {error && <div className="row-spacer text-error">{error}</div>}
          <LoadingButton type="submit" className="niso-button mt-12" isDisabled={isInvalid}>
            Next
          </LoadingButton>
        </form>
      </div>
    </PageLayout>
  );
};

DateOfBirthPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  dateOfBirth: PropTypes.shape({
    day: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    month: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
};

export const mapStateToProps = reduxStore => ({
  dateOfBirth: reduxStore.builder.ui.dateOfBirth,
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { DateOfBirthPage };

export default connect(mapStateToProps, mapDispatchToProps)(DateOfBirthPage);
